import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { DialogActions, DialogShell } from './dialog';

export interface ConfirmRequest {
  title: string;
  /** The question, plus whatever the reader needs to weigh it. */
  body: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
}

/**
 * Asks a yes/no question in the app's own dialog chrome.
 *
 * `window.confirm` freezes the tab, cannot be themed, and in most browsers
 * offers "prevent this page from creating further dialogues" — a checkbox that
 * answers no to every later question without telling anyone. A liability
 * statement and a warning about unsaved work are the two things here worth
 * asking, so neither should be the thing a browser quietly starts declining.
 *
 * Returns the asker and the element to render. The asker resolves true when
 * confirmed, and false on cancel, dismissal or unmount — so a caller awaiting
 * it is never left hanging.
 */
export function useConfirm(): [(request: ConfirmRequest) => Promise<boolean>, ReactNode] {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const resolveRef = useRef<((answer: boolean) => void) | null>(null);

  const settle = useCallback((answer: boolean) => {
    resolveRef.current?.(answer);
    resolveRef.current = null;
    setRequest(null);
  }, []);

  // A question still on screen when the screen goes is answered no, rather
  // than leaving its caller awaiting a promise nothing will ever settle.
  useEffect(() => () => resolveRef.current?.(false), []);

  const confirm = useCallback((next: ConfirmRequest) => {
    resolveRef.current?.(false);
    setRequest(next);
    return new Promise<boolean>((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const dialog = (
    <DialogShell
      open={request !== null}
      title={request?.title ?? ''}
      onClose={() => settle(false)}
      width="max-w-md"
      footer={
        request ? (
          <DialogActions
            onCancel={() => settle(false)}
            onConfirm={() => settle(true)}
            confirmLabel={request.confirmLabel ?? 'Continue'}
            busyLabel={request.confirmLabel ?? 'Continue'}
            cancelLabel={request.cancelLabel ?? 'Cancel'}
          />
        ) : null
      }
    >
      {/* `whitespace-pre-line` so a caller can pass the plain multi-line string
          it used to hand `window.confirm` and have it read the same way. */}
      {typeof request?.body === 'string' ? (
        <p className="m-0 whitespace-pre-line text-sm text-hilevel-slate">{request.body}</p>
      ) : (
        (request?.body ?? null)
      )}
    </DialogShell>
  );

  return [confirm, dialog];
}
